import { put, call, takeLatest, getContext } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { manuallyIncrementPromiseCounter, manuallyResetPromiseCounter, trackPromise } from 'react-promise-tracker';
import { optimizelyActions } from './optimizely.redux';
import {
  IGetOptimizelyFlagsAction,
  IGetOptimizelyFlagVariationAction,
  IOptimizelyFlag,
  IOptimizelyFlags,
} from 'app/models/Optimizely/Optimizely';
import { SharedService } from 'app/services/SharedServices/shared-service';
import { PromiseTrackerKeys } from 'app/constants/PromiseTrackerKeys';
import { httpSuccess } from 'app/services/serviceHelpers';
import { optimizelyInitialState, optimizelyVariationInitialState } from './initialState';
import { trackException } from 'app-insights/appInsightsTracking';

const TrackOptimizelyFlags = (fn, ...args) => trackPromise(fn(...args), PromiseTrackerKeys.global.optimizelyFlags);
const TrackOptimizelyFlagVariation = (fn, ...args) =>
  trackPromise(fn(...args), PromiseTrackerKeys.global.optimizelyFlagVariation);

export function* getOptimizelyFlags(action: PayloadAction<IGetOptimizelyFlagsAction>) {
  try {
    const sharedService: SharedService = yield getContext('sharedService');

    const response = yield call(TrackOptimizelyFlags, sharedService.getOptimizelyFlags, action.payload);
    const flags: IOptimizelyFlag[] = [];
    if (httpSuccess(response.status)) {
      const data = response.data;
      const items: any[] = data.items ?? [];
      items?.forEach(item => {
        const flag: IOptimizelyFlag = {
          key: item.key,
          name: item.name,
          description: item.description,
          environments: {
            dev: {
              enabled: item.environments.dev.enabled,
            },
            qa: {
              enabled: item.environments.qa.enabled,
            },
            int: {
              enabled: item.environments.int.enabled,
            },
            train: {
              enabled: item.environments.train.enabled,
            },
            prod: {
              enabled: item.environments.prod.enabled,
            },
          },
          createdByUserId: item.createdByUserId,
          updatedTime: item.updatedTime,
          createdTime: item.createdTime,
          revision: item.revision,
          variations: undefined,
        };
        flags.push(flag);
      });
      const allFlags: IOptimizelyFlags = {
        items: flags,
        totalPages: data.totalPages,
        totalCount: data.totalCount,
        page: data.page,
        count: data.count,
      };
      yield put(optimizelyActions.setOptimizelyFlags(allFlags));
    } else {
      yield put(optimizelyActions.setOptimizelyFlags({ ...optimizelyInitialState, error: true }));
    }
  } catch (error) {
    trackException({ exception: error });
  }
}

export function* getOptimizelyVariation(action: PayloadAction<IGetOptimizelyFlagVariationAction>) {
  try {
    const sharedService: SharedService = yield getContext('sharedService');
    manuallyIncrementPromiseCounter(PromiseTrackerKeys.global.optimizelyFlagVariation);

    const response = yield call(TrackOptimizelyFlagVariation, sharedService.getOptimizelyFlagVariant, action.payload);
    if (httpSuccess(response.status)) {
      yield put(
        optimizelyActions.setOptimizelyFlagVariation({ key: action.payload.flagKey, variation: response.data }),
      );
    } else {
      yield put(
        optimizelyActions.setOptimizelyFlagVariation({
          key: action.payload.flagKey,
          variation: { ...optimizelyVariationInitialState, error: true },
        }),
      );
    }
    manuallyResetPromiseCounter(PromiseTrackerKeys.global.optimizelyFlagVariation);
  } catch (error) {
    manuallyResetPromiseCounter(PromiseTrackerKeys.global.optimizelyFlagVariation);
    trackException({ exception: error });
  }
}

export function* optimizelySaga() {
  yield takeLatest(optimizelyActions.getOptimizelyFlags.type, getOptimizelyFlags);
  yield takeLatest(optimizelyActions.getOptimizelyFlagVariation.type, getOptimizelyVariation);
}
